import type { ReactNode } from "react";
import { createRootRouteWithContext, HeadContent, Outlet, Scripts } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { QueryClient } from "@tanstack/react-query";

import appCss from "@/styles/app.css?url";
import faviconUrl from "@/assets/favicon.png?url";
import faviconDarkUrl from "@/assets/favicon-dark.png?url";
import { ThemeProvider } from "next-themes";
import { fetchUser } from "@/server/auth/user";
import { Toaster } from "@/components/ui/toaster";

const normalizeAssetUrl = (url: string) => {
  return url.replace(/\?t=\d+/, "");
};

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no",
      },
      {
        title: "MMX Admin",
      },
    ],
    links: [
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: `${normalizeAssetUrl(faviconUrl)}?v=2`,
        media: "(prefers-color-scheme: light)",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: `${normalizeAssetUrl(faviconDarkUrl)}?v=2`,
        media: "(prefers-color-scheme: dark)",
      },
    ],
  }),
  beforeLoad: async () => {
    const user = await fetchUser();

    if (!user) {
      return {
        redirect: "/_auth/login",
      };
    }

    return { user };
  },
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html suppressHydrationWarning>
      <head>
        <HeadContent />
        <link rel="stylesheet" href={normalizeAssetUrl(appCss)} suppressHydrationWarning />
      </head>
      <body suppressHydrationWarning>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          themes={["light", "dark", "system", "classic"]}
          storageKey="mmx-admin-theme"
          enableSystem
          enableColorScheme
          disableTransitionOnChange
        >
          {children}
          <Toaster position="top-center" richColors expand={true} visibleToasts={10} />
        </ThemeProvider>
        <TanStackRouterDevtools position="bottom-right" />
        <Scripts />
      </body>
    </html>
  );
}
